import type { QueryResult, OperationVariables } from '@apollo/react-common';
import {
	type QueryLazyOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useLazyQuery,
} from '@apollo/react-hooks';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { type ConcreteRequest } from 'relay-runtime';
import { type DefinitionNode } from 'graphql';

import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';

import {
	ApolloErrorInterop,
	isApolloError,
} from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import { fetchQuery } from './fetchQuery.ts';
import { isRelayEnabled } from './isRelayEnabled.ts';
import { mapFetchPolicyForQuery } from './mapFetchPolicyForQuery.ts';
import { NetworkStatus } from './NetworkStatus.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { useMemoObj } from './useMemoObj.ts';
import {
	type FetchMoreOptions,
	type FetchMoreType,
	type UseQueryInteropSupportedHookOptions,
} from './useQueryInterop.ts';

export type UseLazyQueryInteropSupportedOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = UseQueryInteropSupportedHookOptions<TData, TVariables>;

export type UseLazyQueryInteropSupportedReturnType<TData, TVariables> = [
	(options?: QueryLazyOptions<TVariables>) => void,
	UseLazyQueryInteropSupportedResult<TData, TVariables>,
];

export type UseLazyQueryInteropSupportedResult<TData, TVariables> = Pick<
	QueryResult<TData, TVariables>,
	'data' | 'loading' | 'variables' | 'error' | 'called' | 'fetchMore' | 'stopPolling'
>;

/** Synced-each-render ref; no microtask lag. */
function useSyncedRef<T>(value: T) {
	const ref = useRef(value);
	ref.current = value;
	return ref;
}

/**
 * Coerce an unknown thrown value into an ApolloErrorInterop so callers can
 * read Apollo's `graphQLErrors` / `networkError` fields uniformly across the
 * Apollo and Relay branches.
 */
function toApolloError(err: unknown): ApolloErrorInterop {
	if (isApolloError(err as Error)) return err as ApolloErrorInterop;
	const networkError = err instanceof Error ? err : new Error(String(err));
	return new ApolloErrorInterop({ networkError });
}

function getOperationName(node: GraphQLTaggedNode): string {
	if ('definitions' in node) {
		const def = (node as { definitions: readonly DefinitionNode[] }).definitions?.find(
			(d): d is DefinitionNode & { name?: { value: string } } =>
				d.kind === 'OperationDefinition' && 'name' in d,
		);
		return def?.name?.value ?? 'unknown';
	}
	return 'unknown';
}

export function useLazyQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: UseLazyQueryInteropSupportedOptions<TData, TVariables>,
): UseLazyQueryInteropSupportedReturnType<TData, TVariables> {
	if (isRelayEnabled(options?.__callerPackage__)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- branch stable per build
		return useLazyQueryInteropRelay<TData, TVariables>(query, options);
	}
	const { interopApolloAggClient, ...apolloOptions } = options ?? {};
	// eslint-disable-next-line react-hooks/rules-of-hooks -- branch stable per build
	return useLazyQuery<TData, TVariables>(toDocumentNode(query), {
		...apolloOptions,
		...(interopApolloAggClient
			? {
					client:
						window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'],
				}
			: {}),
		context: {
			...apolloOptions?.context,
			requestId: (query as ConcreteRequest).params?.id,
		},
	});
}

function useLazyQueryInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: UseLazyQueryInteropSupportedOptions<TData, TVariables>,
): UseLazyQueryInteropSupportedReturnType<TData, TVariables> {
	// Validate before any hook call so the throw isn't masked by useRelayEnvironment().
	if (!('params' in query)) {
		throw new Error(
			`[Relay migration] unsupported tagged node provided for operation "${getOperationName(query)}". Please make sure you generated Relay artifacts for your operation`,
		);
	}

	const environment = useRelayEnvironment();
	const hookVariables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__);
	const hookContext = options?.context;
	const initialFetchPolicy = options?.fetchPolicy
		? mapFetchPolicyForQuery(options.fetchPolicy)
		: undefined;
	const subsequentFetchPolicy = options?.nextFetchPolicy
		? mapFetchPolicyForQuery(options.nextFetchPolicy)
		: initialFetchPolicy;

	const onError = useStableCallback(options?.onError ?? function noop() {});
	const onCompleted = useStableCallback(options?.onCompleted ?? function noop() {});

	const [data, setData] = useState<TData | undefined>(undefined);
	const [error, setError] = useState<ApolloErrorInterop | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [called, setCalled] = useState(false);
	// Mirrors Apollo's `variables` from the most recent invocation.
	const [activeVariables, setActiveVariables] = useState<TVariables>(hookVariables);

	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Sequencing counters discard out-of-order responses.
	const executeIdRef = useRef(0);
	const fetchMoreIdRef = useRef(0);

	// hookContext is unmemoised; data/activeVariables can change mid-fetchMore via a concurrent execute.
	const hookContextRef = useSyncedRef(hookContext);
	const dataRef = useSyncedRef(data);
	const activeVarsRef = useSyncedRef(activeVariables);

	const ufoContext = useContext(UFOInteractionContext);
	const operationName = `relay_${(query as ConcreteRequest).params?.name ?? 'unnamed'}`;
	useEffect(() => {
		if (called && loading) {
			return ufoContext?.hold(operationName);
		}
	}, [called, loading, operationName, ufoContext]);

	const execute = useCallback(
		(executeOptions?: QueryLazyOptions<TVariables>) => {
			const variables = (executeOptions?.variables ?? hookVariables) as TVariables;
			const context = { ...hookContextRef.current, ...executeOptions?.context };

			// First execute uses fetchPolicy, re-invocations use nextFetchPolicy.
			const isFirstCall = executeIdRef.current === 0;
			executeIdRef.current += 1;
			// Invalidate any in-flight fetchMore so it can't merge into fresh execute data.
			fetchMoreIdRef.current += 1;
			const id = executeIdRef.current;
			const isStillCurrent = () => isMountedRef.current && id === executeIdRef.current;

			setCalled(true);
			setActiveVariables(variables);
			setLoading(true);
			setError(undefined);

			fetchQuery<{ response: TData; variables: TVariables }>(environment, query, variables, {
				fetchPolicy: isFirstCall ? initialFetchPolicy : subsequentFetchPolicy,
				networkCacheConfig: { metadata: context },
			})
				.toPromise()
				.then((result) => {
					if (!isStillCurrent()) return;
					setLoading(false);
					if (result !== undefined) {
						setData(result as TData);
						setError(undefined);
						onCompleted(result as TData);
						return;
					}
					// Store-only cache miss: surface so callers aren't left indeterminate.
					const err = toApolloError(
						new Error(
							'[Relay migration] fetchQuery resolved with no data (likely a store-only cache miss)',
						),
					);
					setError(err);
					onError(err);
				})
				.catch((rawErr: unknown) => {
					if (!isStillCurrent()) return;
					const err = toApolloError(rawErr);
					setError(err);
					setLoading(false);
					onError(err);
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refs (hookContextRef) have stable identity, omitted intentionally
		[
			environment,
			query,
			hookVariables,
			initialFetchPolicy,
			subsequentFetchPolicy,
			onCompleted,
			onError,
		],
	);

	const fetchMore: FetchMoreType<TData, TVariables> = useCallback(
		(fetchMoreOpts: FetchMoreOptions<TData, TVariables>) => {
			const variables = {
				...activeVarsRef.current,
				...fetchMoreOpts.variables,
			} as TVariables;

			fetchMoreIdRef.current += 1;
			const id = fetchMoreIdRef.current;
			const isStillCurrent = () => isMountedRef.current && id === fetchMoreIdRef.current;

			setLoading(true);

			return fetchQuery<{ response: TData; variables: TVariables }>(environment, query, variables, {
				// fetchMore must always hit the network (matches Apollo); cached pages would be stale.
				fetchPolicy: 'network-only',
				networkCacheConfig: { metadata: hookContextRef.current },
			})
				.toPromise()
				.then((fetchMoreResult) => {
					// Throw so .catch handles it (avoids double onError).
					if (fetchMoreResult === undefined) {
						throw new Error(
							'[Relay migration] fetchMore resolved with no data (likely a store-only cache miss)',
						);
					}
					const merged = fetchMoreOpts.updateQuery(dataRef.current ?? ({} as TData), {
						fetchMoreResult: fetchMoreResult as TData,
						variables,
					});
					if (isStillCurrent()) {
						setData(merged);
						setLoading(false);
						setError(undefined);
					}
					return {
						data: merged,
						loading: false,
						networkStatus: NetworkStatus.ready,
						stale: false,
					};
				})
				.catch((rawErr: unknown) => {
					// onError gated by isStillCurrent (matches execute); re-throw for Apollo's contract.
					const err = toApolloError(rawErr);
					if (isStillCurrent()) {
						setError(err);
						setLoading(false);
						onError(err);
					}
					throw err;
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refs (activeVarsRef, dataRef, hookContextRef) have stable identity, omitted intentionally
		[environment, query, onError],
	);

	// Lazy hooks don't auto-poll; no-op for Apollo signature parity.
	const stopPolling = useCallback(() => {}, []);

	// Cast: Apollo's overloaded fetchMore is structurally a superset of FetchMoreType.
	return [
		execute,
		{
			data,
			loading,
			error,
			called,
			variables: activeVariables,
			fetchMore,
			stopPolling,
		} as unknown as UseLazyQueryInteropSupportedResult<TData, TVariables>,
	];
}
